import { useState, useMemo, useEffect } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { cn } from "@/utils/utils";
import { getNow } from "@/utils/dateService";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export interface TMonthYearPickerProps {
    /** Value in YYYY-MM format */
    value: string;
    /** Called with YYYY-MM format */
    onChange: (value: string) => void;
    id?: string;
    /**
     * Id of the visible field label element. Use when the root is not a native label target
     * (e.g. inline mode uses a div) so assistive tech and Playwright get_by_label resolve the name.
     */
    ariaLabelledBy?: string;
    required?: boolean;
    /** Disables the control: the popover trigger gets the native `disabled` attribute (not focusable, cannot open the popover). */
    disabled?: boolean;
    className?: string;
    /**
     * When true, the year navigation and month grid are always visible (no trigger row / popover).
     */
    inline?: boolean;
    /**
     * When true, an empty `value` means no selection (trigger shows `emptyLabel` / default).
     * When false, invalid/empty values fall back to the current month for display.
     */
    allowEmpty?: boolean;
    /** Label on the popover trigger when `allowEmpty` and no month is selected */
    emptyLabel?: string;
    /** Earliest selectable month, `YYYY-MM`. Earlier months render disabled. Unbounded when omitted. */
    minMonth?: string;
    /** Latest selectable month, `YYYY-MM`. Later months render disabled. Unbounded when omitted. */
    maxMonth?: string;
}

function parseYmStrict(value: string): { year: number; month: number } | null {
    if (!/^\d{4}-\d{2}$/.test(value)) return null;
    const parts = value.split("-");
    const year = parseInt(parts[0]!, 10);
    const month = parseInt(parts[1]!, 10);
    if (month < 1 || month > 12) return null;
    return { year, month };
}

function parseValue(value: string, allowEmpty: boolean): { year: number; month: number } | null {
    const strict = parseYmStrict(value);
    if (strict) return strict;
    if (allowEmpty && value.trim() === "") return null;
    const now = getNow();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

const MONTHS_PER_YEAR = 12;

/** Sortable key for a month, so range checks are integer comparisons. */
function monthKey(year: number, month: number): number {
    return year * MONTHS_PER_YEAR + (month - 1);
}

function boundKey(value: string | undefined): number | null {
    if (value === undefined) return null;
    const parsed = parseYmStrict(value);
    return parsed === null ? null : monthKey(parsed.year, parsed.month);
}

function MonthGrid({
    viewYear,
    onViewYearChange,
    selection,
    onMonthSelect,
    gridClassName,
    minKey,
    maxKey,
}: {
    viewYear: number;
    onViewYearChange: (delta: number) => void;
    selection: { year: number; month: number } | null;
    onMonthSelect: (monthIndex: number) => void;
    gridClassName: string;
    minKey: number | null;
    maxKey: number | null;
}) {
    // A year is reachable when it still contains a selectable month, so the arrows
    // stop at the ends of the range instead of stranding the user on an all-disabled grid.
    const minYear = minKey === null ? null : Math.floor(minKey / MONTHS_PER_YEAR);
    const maxYear = maxKey === null ? null : Math.floor(maxKey / MONTHS_PER_YEAR);
    const previousDisabled = minYear !== null && viewYear - 1 < minYear;
    const nextDisabled = maxYear !== null && viewYear + 1 > maxYear;

    return (
        <>
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => onViewYearChange(-1)}
                    disabled={previousDisabled}
                    className="p-1 rounded-md hover:bg-muted transition-colors disabled:pointer-events-none disabled:opacity-40"
                    aria-label="Previous year"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold tabular-nums">{viewYear}</span>
                <button
                    type="button"
                    onClick={() => onViewYearChange(1)}
                    disabled={nextDisabled}
                    className="p-1 rounded-md hover:bg-muted transition-colors disabled:pointer-events-none disabled:opacity-40"
                    aria-label="Next year"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>
            <div className={gridClassName}>
                {MONTHS.map((month, i) => {
                    const key = monthKey(viewYear, i + 1);
                    const outOfRange =
                        (minKey !== null && key < minKey) || (maxKey !== null && key > maxKey);
                    const isSelected =
                        selection !== null &&
                        viewYear === selection.year &&
                        i + 1 === selection.month;
                    return (
                        <button
                            key={month}
                            type="button"
                            onClick={() => onMonthSelect(i)}
                            disabled={outOfRange}
                            className={cn(
                                "rounded-lg border border-border py-2 text-sm font-medium transition-colors",
                                isSelected
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-muted/40 hover:bg-muted",
                                "disabled:pointer-events-none disabled:opacity-40",
                            )}
                        >
                            {month}
                        </button>
                    );
                })}
            </div>
        </>
    );
}

export function MonthYearPicker({
    value,
    onChange,
    id,
    ariaLabelledBy,
    required,
    disabled = false,
    className,
    inline = false,
    allowEmpty = false,
    emptyLabel,
    minMonth,
    maxMonth,
}: TMonthYearPickerProps) {
    const selection = useMemo(() => parseValue(value, allowEmpty), [value, allowEmpty]);
    const minKey = useMemo(() => boundKey(minMonth), [minMonth]);
    const maxKey = useMemo(() => boundKey(maxMonth), [maxMonth]);
    const [viewYear, setViewYear] = useState(() => selection?.year ?? getNow().getFullYear());
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setViewYear(selection?.year ?? getNow().getFullYear());
    }, [selection?.year]);

    const handleMonthSelect = (monthIndex: number) => {
        const month = String(monthIndex + 1).padStart(2, "0");
        onChange(`${viewYear}-${month}`);
        setOpen(false);
    };

    const displayLabel =
        selection === null
            ? (emptyLabel ?? "Select month")
            : `${MONTHS[selection.month - 1]} ${selection.year}`;

    if (inline) {
        return (
            <div
                id={id}
                role={ariaLabelledBy ? "group" : undefined}
                aria-labelledby={ariaLabelledBy}
                className={cn(
                    "rounded-xl border border-border bg-background p-4 space-y-3",
                    className,
                )}
            >
                <MonthGrid
                    viewYear={viewYear}
                    onViewYearChange={(delta) => setViewYear((y) => y + delta)}
                    selection={selection}
                    onMonthSelect={handleMonthSelect}
                    gridClassName="grid grid-cols-4 gap-2"
                    minKey={minKey}
                    maxKey={maxKey}
                />
            </div>
        );
    }

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    id={id}
                    type="button"
                    disabled={disabled}
                    className={cn(
                        "flex w-full items-center justify-between px-3 py-2 border rounded-lg bg-background text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary disabled:pointer-events-none disabled:opacity-50",
                        selection === null && "text-muted-foreground",
                        className,
                    )}
                    aria-labelledby={ariaLabelledBy}
                    aria-required={required}
                >
                    <span>{displayLabel}</span>
                    <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    className="z-[60] w-[280px] rounded-xl border bg-popover p-4 shadow-lg animate-in fade-in-0 zoom-in-95"
                    sideOffset={4}
                    align="start"
                >
                    <div className="space-y-3">
                        {allowEmpty && selection !== null ? (
                            <button
                                type="button"
                                onClick={() => {
                                    onChange("");
                                    setOpen(false);
                                }}
                                className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                            >
                                Clear selection
                            </button>
                        ) : null}
                        <MonthGrid
                            viewYear={viewYear}
                            onViewYearChange={(delta) => setViewYear((y) => y + delta)}
                            selection={selection}
                            onMonthSelect={handleMonthSelect}
                            gridClassName="grid grid-cols-3 gap-2"
                            minKey={minKey}
                            maxKey={maxKey}
                        />
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
